import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatisticsFilter from '../StatisticsFilter';
import StatisticsTableComponent from '../StatisticsTable/StatisticsTable.container';
import StatisticsMetricsComponent from '../StatisticsMetrics/StatisticsMetrics.container';
import StatisticsSegmentsComponent from '../StatisticsSegments/StatisticsSegments.container';
import StatisticsChart from '../StatisticsChart/StatisticsChart.container';
import Pagination from '../../../Pagination';
import StatisticsCSV from '../StatisticsCSV/StatisticsCSV.container';
import Loading from '../../../Loading';
import config from '../../../../config';
import objectHasChanged from '../../../../helpers/objectHasChanged';
import query from '../../../../helpers/query';
import localStorage from "../../../../helpers/localStorage";
import ReportComponent from '../../../../utils/ReportComponent/ReportComponent';
import CustomTooltip from '../../../Tooltip.js';
import TZ from '../../../../utils/Timezone/Timezone.js';

const defDataSort = {
    sort: 'GroupBy',
    order: 'desc'
};

const nameLocalStorage = 'showChart';

import { useTemplateConfig } from '../../../../context/TemplateConfigContext';
import { isSameFilterValues } from '../../../../helpers/reportFilterReset';

const reportUtils = ReportComponent.utils;

const StatisticsContent = ({ 
    location, 
    metrics: metricsProp, 
    segments: propSegments, 
    page, 
    totalPages, 
    loadingData, 
    search, 
    searchChart, 
    t,
    user,
    serverStorageReady
}) => {
    const { assets, componentOverrides = {} } = useTemplateConfig();
    const ResolvedStatisticsTableComponent = componentOverrides.StatisticsTableComponent || StatisticsTableComponent;
    const staticNames = (t && t.reportPage) || {};

    const parsedQuery = useMemo(() => query.parse(location && location.query), [location]);
    const hasQueryMetrics = Boolean(parsedQuery.metrics);
    const defaultMetricsRef = useRef(reportUtils.getStrMetric());

    const getDateFromString = useCallback((date) => {
        if (date) {
            date = new Date(date);
            return moment().set({'year': date.getFullYear(), 'month': date.getMonth(), 'date': date.getDate()});
        }
        return false;
    }, []);

    const getInitialStartDate = useCallback(() => {
        let startDate = getDateFromString(parsedQuery.startDate);
        if (startDate) {
            startDate = startDate.startOf('day');
        } else {
            startDate = moment().subtract(6, 'days').startOf('day');

        }
        return startDate;
    }, [parsedQuery.startDate, getDateFromString]);

    const getInitialEndDate = useCallback(() => {
        let endDate = getDateFromString(parsedQuery.endDate);
        if (endDate) {
            endDate = endDate.endOf('day');
        } else {
            endDate = moment().endOf('day');

        }
        return endDate;
    }, [parsedQuery.endDate, getDateFromString]);


    const [state, setState] = useState(() => {
        if (!localStorage.get(nameLocalStorage)) {
            localStorage.set(nameLocalStorage, config.defaultShowChart);
        }

        return {
            startDate: getInitialStartDate(),
            endDate: getInitialEndDate(),
            bindActions: parsedQuery.bindActions || config.defaultBindActions,
            groupBy: parsedQuery.groupBy || config.defaultGroupBy,
            count: parsedQuery.count || config.defaultCount,
            metrics: parsedQuery.metrics || metricsProp || '',
            sort: parsedQuery.sort || defDataSort.sort,
            order: parsedQuery.order || defDataSort.order,
            segments: parsedQuery.segments || propSegments || {},
            page: parsedQuery.page || +page,
            // a timezone from the URL the report API does not accept (not a whole-hour offset) is replaced
            timezone: TZ.toSupportedTimezone(parsedQuery.timezone || user.timezone),
            onlyForTrafficInPeriod: parsedQuery.onlyForTrafficInPeriod ? 1 : 0,
            groupByChart: '',
            totalPages: totalPages,
            loadingData: loadingData,
            showChart: 'show' === localStorage.get(nameLocalStorage)
        };
    });

    const stateRef = useRef(state);
    const searchRef = useRef(search);
    const searchChartRef = useRef(searchChart);
    useLayoutEffect(() => {
        stateRef.current = state;
        searchRef.current = search;
        searchChartRef.current = searchChart;
    }, [state, search, searchChart]);

    const isStateSearchChanged = useMemo(() => {
        return objectHasChanged(['metrics', 'groupByChart', 'page', 'totalPages', 'loadingData'], true, state);
    }, [state]);

    const pushQuery = useCallback(() => {
        const current = stateRef.current;
        query.replace({
            startDate: current.startDate.clone().startOf('day').format(config.reportHourFormat),
            endDate: current.endDate.clone().endOf('day').format(config.reportHourFormat),
            groupBy: current.groupBy || null,
            count: current.count || null,
            bindActions: current.bindActions,
            timezone: current.timezone,
            onlyForTrafficInPeriod: current.onlyForTrafficInPeriod || null,
            sort: current.sort,
            order: current.order,
            metrics: current.metrics || null,
            page: current.page,
            segments: current.segments && Object.keys(current.segments || {}).length ? JSON.stringify(current.segments) : null
        });
    }, []);

    useEffect(() => {
        if (hasQueryMetrics) {
            return;
        }

        if (typeof metricsProp === 'string' && metricsProp.length) {
            setState(prevState => {
                if (prevState.metrics === metricsProp) {
                    return prevState;
                }

                const next = {
                    ...prevState,
                    metrics: metricsProp
                };
                stateRef.current = next;
                return next;
            });
            return;
        }

        if (serverStorageReady && !state.metrics) {
            const fallback = defaultMetricsRef.current;
            setState(prevState => {
                if (prevState.metrics === fallback) {
                    return prevState;
                }

                const next = {
                    ...prevState,
                    metrics: fallback
                };
                stateRef.current = next;
                return next;
            });
        }
    }, [metricsProp, serverStorageReady, hasQueryMetrics, state.metrics]);

    const onSearch = useCallback((typeAction) => {
        const current = stateRef.current;
        if (!current.metrics) {
            return;
        }
        const currentState = {
            startDate: current.startDate,
            endDate: current.endDate,
            bindActions: current.bindActions,
            timezone: current.timezone,
            onlyForTrafficInPeriod: current.onlyForTrafficInPeriod,
            groupBy: current.groupBy,
            count: current.count,
            metrics: current.metrics,
            sort: current.sort,
            order: current.order,
            segments: current.segments,
            page: current.page,
            groupByChart: ''
        };

        if (current.groupByChart !== '') {
            setState(prevState => ({
                ...prevState,
                groupByChart: '',
            }));
        }

        if (searchRef.current) {
            searchRef.current(currentState);
        }

        if (typeAction !== 'table' && typeAction !== 'pagination' && searchChartRef.current) {
            searchChartRef.current(currentState);
        }
    }, []);

    const onUpdateDate = useCallback((segmentsState) => {
        const current = stateRef.current;
        if (current.loadingData) {
            return;
        }

        let segmentsData = {};
        if (segmentsState && segmentsState.data) {
            (segmentsState.data || []).forEach((item) => {
                if (item.segment && item.values && item.values.length) {
                    segmentsData[item.segment] = {
                        exclusive: item.exclusive,
                        values: item.values
                    };
                }
            });
        }

        const isStateChanged = objectHasChanged(['metrics', 'groupByChart', 'page', 'totalPages', 'loadingData'], true, current);
        setState(prevState => {
            const newState = {
                ...prevState,
                segments: segmentsData,
                page: isStateChanged ? config.initialPage : prevState.page,
                ...defDataSort
            };
            stateRef.current = newState;
            return newState;
        });

        setTimeout(() => {
            const updated = stateRef.current;
            onSearch();
            pushQuery();
        }, 0);
    }, [onSearch, pushQuery]);

    // skips the echo of a reset from the top filter (see helpers/reportFilterReset)
    const resetEchoRef = useRef(null);

    const onActionFilter = useCallback((filterState) => {
        const resetValues = resetEchoRef.current;
        resetEchoRef.current = null;
        if (resetValues && isSameFilterValues(resetValues, filterState)) {
            return;
        }

        setState(prevState => {
            const newState = {
                ...prevState,
                page: config.initialPage,
                ...filterState
            };
            stateRef.current = newState;
            
            setTimeout(() => {
                if (searchRef.current) {
                    searchRef.current(newState);
                }
                if (searchChartRef.current) {
                    searchChartRef.current(newState);
                }
                
                query.replace({
                    startDate: newState.startDate.clone().startOf('day').format(config.reportHourFormat),
                    endDate: newState.endDate.clone().endOf('day').format(config.reportHourFormat),
                    timezone: newState.timezone,
                    groupBy: newState.groupBy || null,
                    count: newState.count || null,
                    bindActions: newState.bindActions,
                    onlyForTrafficInPeriod: newState.onlyForTrafficInPeriod || null,
                    sort: newState.sort,
                    order: newState.order,
                    metrics: newState.metrics || null,
                    page: newState.page,
                    segments: newState.segments && Object.keys(newState.segments || {}).length ? JSON.stringify(newState.segments) : null
                });
            }, 0);
            
            return newState;
        });
    }, []);

    // Reset all report filters (top filters and segments) to defaults and reload
    const onResetAll = useCallback(() => {
        const defaults = {
            startDate: moment().subtract(6, 'days').startOf('day'),
            endDate: moment().endOf('day'),
            bindActions: config.defaultBindActions,
            groupBy: config.defaultGroupBy,
            count: config.defaultCount,
            timezone: TZ.toSupportedTimezone(user.timezone),
            onlyForTrafficInPeriod: 0
        };

        onActionFilter({
            ...defaults,
            segments: {},
            groupByChart: '',
            ...defDataSort
        });
        resetEchoRef.current = defaults;
    }, [onActionFilter, user.timezone]);

    const onActionMetrics = useCallback((metrics) => {
        setState(prevState => {
            const next = { ...prevState, metrics };
            stateRef.current = next;
            setTimeout(() => {
                const updated = stateRef.current;
                if (searchRef.current) {
                    searchRef.current(updated);
                }
                if (searchChartRef.current) {
                    searchChartRef.current(updated);
                }
                pushQuery();
            }, 0);
            return next;
        });
    }, [pushQuery]);

    const onActionTable = useCallback((dataSort) => {
        setState(prevState => {
            const next = { ...prevState, ...dataSort };
            stateRef.current = next;
            return next;
        });

        onSearch('table');
        pushQuery();
    }, [onSearch, pushQuery]);

    const onPageChange = useCallback((page) => {
        setState(prevState => {
            const next = { ...prevState, page };
            stateRef.current = next;
            return next;
        });

        onSearch('pagination');
        pushQuery();
    }, [onSearch, pushQuery]);

    const onActionSegments = useCallback((segments) => {
        setState(prevState => {
            const next = { ...prevState, segments };
            stateRef.current = next;
            return next;
        });
    }, []);

    const onActionChart = useCallback((chartState) => {
        setState(prevState => {
            const next = { ...prevState, groupByChart: chartState.currentGroupBy };
            if (searchChartRef.current) {
                searchChartRef.current(next);
            }
            return next;
        });
    }, []);

    const hideShowChart = useCallback((e) => {
        e.preventDefault();
        const newShowChart = !state.showChart;
        const current = stateRef.current;
        
        setState(prevState => {
            const next = {
                ...prevState,
                showChart: newShowChart
            };
            stateRef.current = next;
            return next;
        });

        localStorage.set(nameLocalStorage, newShowChart ? 'show' : 'hide');
        
        if (newShowChart && searchChartRef.current) {
            setTimeout(() => {
                const updated = stateRef.current;
                searchChartRef.current(updated);
            }, 0);
        }
    }, [state.showChart]);

    const getChartShowHideIcon = useCallback((showChart) => {
        const altText = showChart ? (staticNames.hide || 'Hide') : (staticNames.show || 'Show');
        return <img src={showChart ? assets.chart.hide : assets.chart.show} alt={altText} title={altText}/>;
    }, [staticNames, assets]);

    const getStatisticsChart = useCallback(() => {
        if (!state.showChart) {
            return null;
        }

        return (
            <div className="ibox">
                <StatisticsChart
                    onAction={onActionChart}
                    groupBy={state.groupByChart}
                />
            </div>
        );
    }, [state.showChart, state.groupByChart, onActionChart]);

    useEffect(() => {
        setState(prevState => {
            if (prevState.totalPages === totalPages && prevState.loadingData === loadingData) {
                return prevState;
            }
            const newState = {
                ...prevState,
                totalPages: totalPages,
                loadingData: loadingData
            };
            stateRef.current = newState;
            return newState;
        });
    }, [totalPages, loadingData]);


    if (!state.metrics) {
        return (
            <section className="wrapper-content statistics">
                <div className="ibox text-center">
                    <Loading center />
                </div>
            </section>
        );
    }

    return (
        <section className="wrapper-content statistics">
            <div className="ibox">
                <StatisticsFilter
                    startDate={state.startDate}
                    endDate={state.endDate}
                    groupBy={state.groupBy}
                    count={state.count}
                    timezone={state.timezone}
                    bindActions={state.bindActions}
                    onlyForTrafficInPeriod={state.onlyForTrafficInPeriod}
                    onAction={onActionFilter}
                />

                <StatisticsSegmentsComponent
                    segments={state.segments}
                    onAction={onActionSegments}
                    onUpdateDate={onUpdateDate}
                    onReset={onResetAll}
                    commonControls={true}
                    loading={state.loadingData}
                />
            </div>

            <div className="ibox-title bold" id="statistics-table-header">
                <CustomTooltip id={`toggleChartTooltip`} tooltip={state.showChart ? (staticNames.hide || 'Hide') : (staticNames.show || 'Show')}>
                    <button
                        className={`show-hide-chart btn`}
                        onClick={hideShowChart}
                    >
                        {getChartShowHideIcon(state.showChart)}
                    </button>
                </CustomTooltip>
                <StatisticsMetricsComponent onAction={onActionMetrics} metrics={state.metrics}/>
                <StatisticsCSV/>
            </div>

            {getStatisticsChart()}

            <div className="ibox">
                <div className="ibox-content no-padding">
                    <ResolvedStatisticsTableComponent
                        sort={state.sort}
                        order={state.order}
                        metrics={state.metrics}
                        onAction={onActionTable}
                    />
                </div>
                <div className="ibox-footer pl0 pr0 pb0">
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={state.page}
                        totalPages={totalPages}
                        loading={state.loadingData}
                    />
                </div>
            </div>
        </section>
    );
};

StatisticsContent.propTypes = {
    location: PropTypes.object,
    metrics: PropTypes.string,
    segments: PropTypes.object,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    loadingData: PropTypes.bool,
    search: PropTypes.func,
    searchChart: PropTypes.func,
    t: PropTypes.object,
    serverStorageReady: PropTypes.bool
};

StatisticsContent.defaultProps = {
    location: {},
    metrics: '',
    segments: {},
    page: 1,
    totalPages: 0,
    loadingData: false,
    search: () => {},
    searchChart: () => {},
    t: {},
    serverStorageReady: false
};

export default withTranslation(StatisticsContent);
